import React, { useCallback, useEffect, useRef, useState } from "react";
import {
    getPortalNotificationCount,
    getPortalNotificationPreferences,
    listPortalNotifications,
    markAllPortalNotificationsRead,
    runPortalNotificationAction,
    savePortalNotificationPreferences,
    updatePortalNotification,
} from "./api";
import {
    disableAdminPushNotifications,
    enableAdminPushNotifications,
    getExistingPushSubscription,
    getPushSupportStatus,
    sendAdminPushNotificationTest,
} from "./pushNotifications";
import {
    PortalClearFiltersIcon,
    PortalFilterGroupControls,
    PortalFilterIcon,
} from "./PortalExperience";
import "./NotificationCentre.css";

const CATEGORIES = ["all", "episodes", "recordings", "meetings", "billing", "integrations", "system"];
const CATEGORY_LABELS = {
    all: "All activity",
    episodes: "Episodes",
    recordings: "Recordings",
    meetings: "Meetings",
    billing: "Billing",
    integrations: "Integrations",
    system: "System",
};

function BellIcon() {
    return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9M10 21h4" /></svg>;
}

function SettingsIcon() {
    return <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.7 1.7 0 0 0 .34 1.88l.06.06-2.83 2.83-.06-.06a1.7 1.7 0 0 0-1.88-.34 1.7 1.7 0 0 0-1.03 1.56V21h-4v-.08A1.7 1.7 0 0 0 9 19.37a1.7 1.7 0 0 0-1.88.34l-.06.06-2.83-2.83.06-.06A1.7 1.7 0 0 0 4.63 15a1.7 1.7 0 0 0-1.56-1.03H3v-4h.08A1.7 1.7 0 0 0 4.63 9a1.7 1.7 0 0 0-.34-1.88l-.06-.06 2.83-2.83.06.06A1.7 1.7 0 0 0 9 4.63a1.7 1.7 0 0 0 1.03-1.56V3h4v.08A1.7 1.7 0 0 0 15 4.63a1.7 1.7 0 0 0 1.88-.34l.06-.06 2.83 2.83-.06.06A1.7 1.7 0 0 0 19.37 9a1.7 1.7 0 0 0 1.56 1.03H21v4h-.08A1.7 1.7 0 0 0 19.4 15Z" /></svg>;
}

function RetryIcon() {
    return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20 6v5h-5" /><path d="M18.5 15a7 7 0 1 1-.8-7.9L20 9" /></svg>;
}

function CheckIcon() {
    return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m5 12 4 4L19 6" /></svg>;
}

function MarkAllReadIcon() {
    return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m2.5 12 3 3 5-6" /><path d="m8.5 12 3 3 5-6" /><path d="m14.5 12 3 3 4-5" /></svg>;
}

function CloseIcon() {
    return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m6 6 12 12M18 6 6 18" /></svg>;
}

function relativeTime(value) {
    const timestamp = new Date(value).getTime();
    if (!Number.isFinite(timestamp)) return "";
    const seconds = Math.max(0, Math.round((Date.now() - timestamp) / 1000));
    if (seconds < 60) return "Just now";
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
    return new Date(value).toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

export default function NotificationCentre({ currentUser, variant = "modern" }) {
    const isAdmin = currentUser?.role === "admin";
    const rootRef = useRef(null);
    const [open, setOpen] = useState(false);
    const [view, setView] = useState("feed");
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [activeFilterId, setActiveFilterId] = useState(null);
    const [category, setCategory] = useState("all");
    const [status, setStatus] = useState("all");
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [busyId, setBusyId] = useState(null);
    const [preferences, setPreferences] = useState([]);
    const [preferencesLoading, setPreferencesLoading] = useState(false);
    const [preferencesSaving, setPreferencesSaving] = useState(false);
    const [systemStatus, setSystemStatus] = useState("checking");
    const [systemMessage, setSystemMessage] = useState("");
    const [systemBusy, setSystemBusy] = useState(false);

    const loadCount = useCallback(async () => {
        if (!isAdmin) return;
        const result = await getPortalNotificationCount();
        if (result?.ok) setUnreadCount(Number(result.unread_count || 0));
    }, [isAdmin]);

    const loadNotifications = useCallback(async () => {
        if (!isAdmin) return;
        setLoading(true);
        setError("");
        const result = await listPortalNotifications({ category, status });
        if (result?.ok) {
            setNotifications(result.notifications || []);
            setUnreadCount(Number(result.unread_count || 0));
        } else {
            setError(result?.message || "Notifications could not be loaded.");
        }
        setLoading(false);
    }, [category, isAdmin, status]);

    useEffect(() => {
        if (!isAdmin) return undefined;
        loadCount();
        const interval = window.setInterval(loadCount, 45_000);
        const handleVisibility = () => {
            if (document.visibilityState === "visible") loadCount();
        };
        document.addEventListener("visibilitychange", handleVisibility);
        return () => {
            window.clearInterval(interval);
            document.removeEventListener("visibilitychange", handleVisibility);
        };
    }, [isAdmin, loadCount]);

    useEffect(() => {
        if (open && view === "feed") loadNotifications();
    }, [loadNotifications, open, view]);

    useEffect(() => {
        if (!open) return undefined;
        const closeOnOutsideClick = (event) => {
            if (!rootRef.current?.contains(event.target)) setOpen(false);
        };
        const closeOnEscape = (event) => {
            if (event.key !== "Escape") return;
            if (activeFilterId) setActiveFilterId(null);
            else if (filtersOpen) setFiltersOpen(false);
            else setOpen(false);
        };
        document.addEventListener("mousedown", closeOnOutsideClick);
        document.addEventListener("keydown", closeOnEscape);
        return () => {
            document.removeEventListener("mousedown", closeOnOutsideClick);
            document.removeEventListener("keydown", closeOnEscape);
        };
    }, [activeFilterId, filtersOpen, open]);

    useEffect(() => {
        if (!filtersOpen) return undefined;
        const closeFiltersOnOutsideClick = (event) => {
            if (!event.target.closest?.(".notification-centre__filter-popover, .notification-centre__filter-button")) {
                setFiltersOpen(false);
                setActiveFilterId(null);
            }
        };
        document.addEventListener("mousedown", closeFiltersOnOutsideClick);
        return () => document.removeEventListener("mousedown", closeFiltersOnOutsideClick);
    }, [filtersOpen]);

    if (!isAdmin) return null;

    async function refreshSystemStatus() {
        const support = getPushSupportStatus();
        if (!support.supported) {
            setSystemStatus("unsupported");
            setSystemMessage(support.reason);
            return;
        }
        if (Notification.permission === "denied") {
            setSystemStatus("blocked");
            setSystemMessage("System notifications are blocked in this browser’s settings.");
            return;
        }
        try {
            const subscription = await getExistingPushSubscription();
            setSystemStatus(subscription ? "enabled" : "disabled");
            setSystemMessage(subscription
                ? "Enabled on this device."
                : "Off on this device. In-portal notifications will still work.");
        } catch {
            setSystemStatus("unsupported");
            setSystemMessage("System notifications are not available on this device.");
        }
    }

    async function showPreferences() {
        setFiltersOpen(false);
        setView("preferences");
        refreshSystemStatus();
        if (preferences.length) return;
        setPreferencesLoading(true);
        setError("");
        const result = await getPortalNotificationPreferences();
        if (result?.ok) setPreferences(result.preferences || []);
        else setError(result?.message || "Preferences could not be loaded.");
        setPreferencesLoading(false);
    }

    async function enableSystemNotifications() {
        setSystemBusy(true);
        setSystemMessage("");
        try {
            const result = await enableAdminPushNotifications();
            if (!result?.ok) {
                setSystemStatus(result?.permission === "denied" ? "blocked" : "disabled");
                setSystemMessage(result?.message || "System notifications could not be enabled.");
            } else {
                setSystemStatus("enabled");
                setSystemMessage("Enabled on this device.");
            }
        } catch (systemError) {
            setSystemStatus("disabled");
            setSystemMessage(systemError?.message || "System notifications could not be enabled.");
        }
        setSystemBusy(false);
    }

    async function disableSystemNotifications() {
        setSystemBusy(true);
        setSystemMessage("");
        try {
            await disableAdminPushNotifications();
            setSystemStatus("disabled");
            setSystemMessage("Off on this device. In-portal notifications will still work.");
        } catch (systemError) {
            setSystemMessage(systemError?.message || "System notifications could not be disabled.");
        }
        setSystemBusy(false);
    }

    async function testSystemNotifications() {
        setSystemBusy(true);
        setSystemMessage("");
        try {
            const result = await sendAdminPushNotificationTest();
            setSystemMessage(result?.ok ? "Test notification sent." : (result?.message || "The test could not be sent."));
        } catch (systemError) {
            setSystemMessage(systemError?.message || "The test could not be sent.");
        }
        setSystemBusy(false);
    }

    async function mutateNotification(id, action) {
        setBusyId(id);
        setError("");
        const result = await updatePortalNotification(id, action);
        if (result?.ok) await loadNotifications();
        else setError(result?.message || "The notification could not be updated.");
        setBusyId(null);
    }

    async function runQuickAction(id) {
        setBusyId(id);
        setError("");
        const result = await runPortalNotificationAction(id);
        if (result?.ok) await loadNotifications();
        else setError(result?.message || "The action could not be completed.");
        setBusyId(null);
    }

    async function markAllRead() {
        const result = await markAllPortalNotificationsRead();
        if (result?.ok) await loadNotifications();
        else setError(result?.message || "Notifications could not be marked as read.");
    }

    function openNotification(item) {
        if (!item.deep_link) return;
        if (item.unread) updatePortalNotification(item.id, "read");
        setOpen(false);
        window.location.href = item.deep_link;
    }

    function togglePreference(index, field) {
        setPreferences((current) => current.map((item, itemIndex) => (
            itemIndex === index ? { ...item, [field]: !item[field] } : item
        )));
    }

    async function savePreferences() {
        setPreferencesSaving(true);
        setError("");
        const result = await savePortalNotificationPreferences(preferences);
        if (result?.ok) {
            setPreferences(result.preferences || []);
            setView("feed");
            await loadNotifications();
        } else {
            setError(result?.message || "Preferences could not be saved.");
        }
        setPreferencesSaving(false);
    }

    return (
        <div className={`notification-centre notification-centre--${variant}`} ref={rootRef}>
            <button
                type="button"
                className="notification-centre__trigger"
                aria-label={unreadCount ? `Notifications, ${unreadCount} unread` : "Notifications"}
                aria-expanded={open}
                title="Notifications"
                onClick={() => { setOpen((value) => !value); setView("feed"); setFiltersOpen(false); }}
            >
                <BellIcon />
                {unreadCount > 0 && <span className="notification-centre__badge">{unreadCount > 99 ? "99+" : unreadCount}</span>}
            </button>

            {open && (
                <section className="notification-centre__panel" role="dialog" aria-label="Notification centre">
                    <header className="notification-centre__header">
                        <div>
                            <button type="button" className="notification-centre__mobile-back" onClick={() => setOpen(false)} aria-label="Close notifications">←</button>
                            <h2>{view === "feed" ? "Notifications" : "Notification preferences"}</h2>
                        </div>
                        {view === "feed" ? (
                            <div className="notification-centre__header-actions">
                                <button
                                    type="button"
                                    className={`beta-search-filter-bar__button beta-search-filter-bar__filter notification-centre__filter-button${category !== "all" || status !== "all" ? " has-active-filters" : ""}`}
                                    onClick={() => { setFiltersOpen((current) => !current); setActiveFilterId(null); }}
                                    aria-label="Filter notifications"
                                    aria-expanded={filtersOpen}
                                    title="Filter notifications"
                                ><PortalFilterIcon /></button>
                                <button
                                    type="button"
                                    className="notification-centre__header-control notification-centre__mark-all-read"
                                    onClick={markAllRead}
                                    aria-label="Mark all notifications as read"
                                    title="Mark all as read"
                                    disabled={unreadCount === 0}
                                ><MarkAllReadIcon /></button>
                                <button type="button" className="notification-centre__header-control" onClick={showPreferences} aria-label="Notification preferences" title="Notification preferences"><SettingsIcon /></button>
                                {filtersOpen && (
                                    <div className="beta-search-filter-bar__popover notification-centre__filter-popover" role="dialog" aria-label="Search filters">
                                        <div className="beta-search-filter-bar__popover-title">
                                            <span>Search filters</span>
                                            <button type="button" className="beta-search-filter-bar__clear" onClick={() => { setCategory("all"); setStatus("all"); setActiveFilterId(null); }} aria-label="Clear all filters" title="Clear all filters"><PortalClearFiltersIcon /></button>
                                        </div>
                                        <div className="beta-search-filter-bar__fields">
                                            <div className="beta-filter-menu__group">
                                                <button type="button" className={`beta-filter-menu__trigger${activeFilterId === "category" ? " is-active" : ""}`} aria-expanded={activeFilterId === "category"} onClick={() => setActiveFilterId(activeFilterId === "category" ? null : "category")}><span>Category</span><span aria-hidden="true">›</span></button>
                                                {activeFilterId === "category" && <div className="beta-filter-menu__submenu"><PortalFilterGroupControls groupLabel="Category" controls={[<select key="category" name="category" value={category} onChange={(event) => setCategory(event.target.value)}>{CATEGORIES.map((item) => <option value={item} key={item}>{CATEGORY_LABELS[item]}</option>)}</select>]} /></div>}
                                            </div>
                                            <div className="beta-filter-menu__group">
                                                <button type="button" className={`beta-filter-menu__trigger${activeFilterId === "status" ? " is-active" : ""}`} aria-expanded={activeFilterId === "status"} onClick={() => setActiveFilterId(activeFilterId === "status" ? null : "status")}><span>Status</span><span aria-hidden="true">›</span></button>
                                                {activeFilterId === "status" && <div className="beta-filter-menu__submenu"><PortalFilterGroupControls groupLabel="Status" controls={[<select key="status" name="status" value={status} onChange={(event) => setStatus(event.target.value)}><option value="all">All</option><option value="unread">Unread</option><option value="open">Open</option><option value="resolved">Resolved</option></select>]} /></div>}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ) : <button type="button" onClick={() => setView("feed")}>Back</button>}
                    </header>

                    {error && <div className="notification-centre__error" role="alert">{error}</div>}

                    {view === "feed" ? (
                        <>
                            <div className="notification-centre__list">
                                {loading && <div className="notification-centre__state">Loading notifications…</div>}
                                {!loading && notifications.length === 0 && <div className="notification-centre__state"><BellIcon /><strong>You’re all caught up</strong></div>}
                                {!loading && notifications.map((item) => (
                                    <article
                                        className={`notification-centre__item${item.unread ? " is-unread" : ""}${item.deep_link ? " is-clickable" : ""}`}
                                        key={item.id}
                                        role={item.deep_link ? "link" : undefined}
                                        tabIndex={item.deep_link ? 0 : undefined}
                                        aria-label={item.deep_link ? `Open ${item.episode_title || item.title}` : undefined}
                                        onClick={() => openNotification(item)}
                                        onKeyDown={(event) => {
                                            if (event.currentTarget === event.target && (event.key === "Enter" || event.key === " ")) {
                                                event.preventDefault();
                                                openNotification(item);
                                            }
                                        }}
                                    >
                                        <span className={`notification-centre__severity notification-centre__severity--${item.severity}`} />
                                        <div className="notification-centre__item-body">
                                            <div className="notification-centre__item-heading">
                                                <strong>{item.title}</strong>
                                                {Number(item.occurrence_count) > 1 && <span className="notification-centre__group-count">×{item.occurrence_count}</span>}
                                                <div className="notification-centre__item-tools">
                                                    <time>{relativeTime(item.last_occurred_at || item.created_at)}</time>
                                                    {item.action_type && !item.resolved && (
                                                        <button
                                                            type="button"
                                                            className={`notification-centre__icon-action${busyId === item.id ? " is-spinning" : ""}`}
                                                            aria-label={busyId === item.id ? "Retrying" : (item.action_label || "Retry")}
                                                            title={busyId === item.id ? "Retrying…" : (item.action_label || "Retry")}
                                                            disabled={busyId === item.id}
                                                            onClick={(event) => { event.stopPropagation(); runQuickAction(item.id); }}
                                                        ><RetryIcon /></button>
                                                    )}
                                                    {item.unread && (
                                                        <button
                                                            type="button"
                                                            className="notification-centre__icon-action"
                                                            aria-label="Mark as read"
                                                            title="Mark as read"
                                                            disabled={busyId === item.id}
                                                            onClick={(event) => { event.stopPropagation(); mutateNotification(item.id, "read"); }}
                                                        ><CheckIcon /></button>
                                                    )}
                                                    <button
                                                        type="button"
                                                        className="notification-centre__icon-action"
                                                        aria-label="Dismiss notification"
                                                        title="Dismiss"
                                                        disabled={busyId === item.id}
                                                        onClick={(event) => { event.stopPropagation(); mutateNotification(item.id, "dismiss"); }}
                                                    ><CloseIcon /></button>
                                                </div>
                                            </div>
                                            {item.message && <p>{item.message}</p>}
                                            {item.episode_title && (
                                                <span className="notification-centre__episode-title">
                                                    Episode: {item.episode_title}
                                                </span>
                                            )}
                                        </div>
                                    </article>
                                ))}
                            </div>
                        </>
                    ) : (
                        <div className="notification-centre__preferences">
                            <p>Use the same categories for the portal and your device. You can turn system notifications off without affecting the notification centre.</p>
                            <section className="notification-centre__system-settings" aria-label="System notification settings">
                                <div>
                                    <strong>System notifications on this device</strong>
                                    <span className={`notification-centre__system-status is-${systemStatus}`}>{systemStatus === "checking" ? "Checking…" : systemStatus}</span>
                                </div>
                                <p>{systemMessage}</p>
                                <div className="notification-centre__system-actions">
                                    {systemStatus === "enabled" ? (
                                        <>
                                            <button type="button" disabled={systemBusy} onClick={testSystemNotifications}>Send test</button>
                                            <button type="button" disabled={systemBusy} onClick={disableSystemNotifications}>Turn off</button>
                                        </>
                                    ) : systemStatus !== "unsupported" && systemStatus !== "blocked" ? (
                                        <button type="button" disabled={systemBusy} onClick={enableSystemNotifications}>{systemBusy ? "Enabling…" : "Turn on"}</button>
                                    ) : null}
                                </div>
                            </section>
                            {preferencesLoading && <div className="notification-centre__state">Loading preferences…</div>}
                            {!preferencesLoading && preferences.map((preference, index) => (
                                <section className="notification-centre__preference-row" key={preference.category}>
                                    <h3>{CATEGORY_LABELS[preference.category]}</h3>
                                    <div className="notification-centre__preference-options">
                                        <label><span>In portal</span><input type="checkbox" checked={preference.in_portal_enabled} onChange={() => togglePreference(index, "in_portal_enabled")} /></label>
                                        <label><span>System</span><input type="checkbox" checked={preference.push_enabled} onChange={() => togglePreference(index, "push_enabled")} /></label>
                                        <label><span>Group similar</span><input type="checkbox" checked={preference.group_similar} onChange={() => togglePreference(index, "group_similar")} /></label>
                                    </div>
                                </section>
                            ))}
                            <button type="button" className="notification-centre__save" disabled={preferencesSaving || preferencesLoading} onClick={savePreferences}>{preferencesSaving ? "Saving…" : "Save preferences"}</button>
                        </div>
                    )}
                </section>
            )}
        </div>
    );
}
